<template>
  <div>
    <el-dialog
      width="632px"
      :visible="true"
      :before-close="handleClose"
      :close-on-click-modal="false"
      :modal-append-to-body="false"
      :modal="false"
    >
      <div slot="title">
        <div class="el-dialog__title">
          {{ $t('CampaignForm_CopyFromDialog_Title') }}
        </div>
      </div>
      <div class="items-container">
        <radio-group v-model="selectedMessage">
          <el-table
            :data="copyList"
            :stripe="true"
            class="table-container outlined"
            :max-height="500"
          >
            <el-table-column :label="$t('CampaignForm_CopyFromDialog_Column')">
              <template slot-scope="scope">
                <radio :label="scope.row" :title="scope.row.title" />
              </template>
            </el-table-column>
          </el-table>
        </radio-group>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          @click="applyCopy"
          :disabled="selectedMessage === null"
        >
          <span>{{ $t('Campaign_Dialog_Button_Apply') }}</span>
          <i class="el-icon-arrow-right el-icon--right" />
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import {
  channelLabels,
  channelTypes
} from '@/modules/campaign-manage/enum/Labels'
import { languages as languagesList } from '@/modules/campaign-manage/enum/LanguagesEnum'
import _ from 'lodash'

export default {
  name: 'CopyFromDialog',
  components: { Radio, RadioGroup },
  props: {
    messageType: {
      type: Number,
      required: true
    },
    showConfirmation: {
      type: Boolean,
      default: false
    },
    currentMessage: {
      type: Object,
      default: null
    },
    model: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      channelTypes,
      selectedMessage: null
    }
  },

  computed: {
    copyRequiredFields() {
      return {
        ios_push: { props: ['push_text', 'push_title'], mode: 'and' },
        android_push: { props: ['push_text', 'push_title'], mode: 'and' },
        web_push: { props: ['push_text', 'push_title'], mode: 'and' },
        inbox: { props: ['push_text', 'push_title'], mode: 'and' },
        email: { props: ['email_html'], mode: 'and' },
        sms: { props: ['sms.text', 'sms.from'], mode: 'or' },
        webhook: { props: ['webhook.url'], mode: 'and' },
        facebook: {
          props: ['facebook.subtitle', 'facebook.title'],
          mode: 'and'
        },
        whatsapp: { props: ['whatsapp.text'], mode: 'and' },
        inapp: {
          props: [
            'inapp.creatives.portrait.creative_0.background_file',
            'inapp.creatives.landscape.creative_0.background_file',
            'inapp.placeholder.portrait.background',
            'inapp.placeholder.landscape.background',
            'inapp.buttons.button_0.landscape.background_file',
            'inapp.buttons.button_0.portrait.background_file',
            'inapp.html',
            'inapp.editor.image',
            'inapp.editor.main_text',
            'inapp.editor.button0.text',
            'inapp.editor.button1.text'
          ],
          mode: 'or'
        },
        onsite: {
          props: [
            'onsite.popup.image',
            'onsite.popup.title',
            'onsite.popup.main_text',
            'push_text',
            'push_title'
          ],
          mode: 'or'
        }
      }
    },
    variant() {
      if (this.$route.query.variant === 'default') return 'A'
      return this.$route.query.variant
    },
    language() {
      if (this.$route.query.lang === 'default') return 'en'
      return this.$route.query.lang
    },
    channelsInverted() {
      return _.invert(channelTypes)
    },
    channel() {
      return this.$route.query.channel
    },
    viceVersaRelatedChannels() {
      return ['android_push', 'ios_push']
    },
    oneSideRelatedChannels() {
      return [
        {
          channel: 'inbox',
          has: ['web_push']
        }
      ]
    },
    allContents() {
      let list = []
      _.forOwn(this.model.messages, (message, msgKey) => {
        _.forOwn(message, (variant, variantKey) => {
          if (this.model.languages_multi === 1) {
            _.forOwn(variant.languages, (content, lang) => {
              list.push(
                this.transform(
                  this.messageType,
                  lang,
                  variantKey,
                  msgKey,
                  content
                )
              )
            })
          } else {
            list.push(
              this.transform(
                this.messageType,
                null,
                variantKey,
                msgKey,
                variant.content
              )
            )
          }
        })
      })
      return list
    },
    filteredContents() {
      return _.filter(this.allContents, msg => {
        return (
          this.isChannelMatch(msg.channel) &&
          this.isVariantLanguageMatch(msg) &&
          this.validateRequiredFields(this.channel, msg.content)
        )
      })
    },
    channels() {
      return {
        ...channelLabels,
        inapp: 'CampaignForm_ChannelsSelect_Inapp',
        workflow: 'CampaignForm_ChannelsSelect_Workflow'
      }
    },
    copyList() {
      return _.forOwn(_.cloneDeep(this.filteredContents), msg => {
        return (msg.title = this.getFullTitle(msg))
      })
    }
  },

  methods: {
    transform(type, lang, variant, msgID, content) {
      return {
        type: type,
        lang: lang,
        channel: this.channelsInverted[msgID],
        variant: variant,
        title: `${this.$t(this.channels[this.channelsInverted[msgID]])}`,
        content: content
      }
    },
    getFullTitle(msg) {
      let title = msg.title
      if (msg.lang) {
        title += ` / ${languagesList[msg.lang]}`
      }
      if (this.model.ab === 1) {
        return (title += ` / ${this.$t('CampaignForm_Common_Title_Variant', {
          variant: msg.variant
        })}`)
      }
      return title
    },
    isChannelMatch(messageChannel) {
      return (
        this.channel === messageChannel ||
        (this.viceVersaRelatedChannels.includes(messageChannel) &&
          this.viceVersaRelatedChannels.includes(this.channel)) ||
        _.filter(this.oneSideRelatedChannels, relation => {
          return (
            relation.channel === this.channel &&
            relation.has.includes(messageChannel)
          )
        }).length > 0
      )
    },
    isVariantLanguageMatch(msg) {
      if (this.model.languages_multi === 1) {
        return (
          this.variant !== msg.variant ||
          this.language !== msg.lang ||
          this.channel !== msg.channel
        )
      } else {
        return this.variant !== msg.variant || this.channel !== msg.channel
      }
    },
    index(obj, is, value) {
      if (typeof is === 'string') {
        return this.index(obj, is.split('.'), value)
      } else if (is.length === 1 && value !== undefined) {
        return (obj[is[0]] = value)
      } else if (is.length === 0) {
        return obj
      } else {
        return this.index(obj[is[0]], is.slice(1), value)
      }
    },
    handleClose() {
      EventBus.$emit('copyFromDialog', null)
    },
    applyCopy() {
      EventBus.$emit('applyCopyMessage', this.selectedMessage)
    },
    isAttributeEmpty(messageObj, attribute) {
      try {
        if (
          (attribute.includes('.') &&
            this.index(messageObj, attribute) === '') ||
          this.index(messageObj, attribute) === null
        ) {
          return true
        } else {
          if (messageObj[attribute] === '' || messageObj[attribute] === null) {
            return true
          }
        }
      } catch (e) {
        return true
      }
      return false
    },
    validateRequiredFields(channel, messageObj) {
      let result = false
      if (this.copyRequiredFields[channel]) {
        _.forEach(this.copyRequiredFields[channel].props, attribute => {
          if (this.copyRequiredFields[channel].mode === 'and') {
            result = true
            if (this.isAttributeEmpty(messageObj, attribute)) {
              result = false
              return true
            }
          } else if (this.copyRequiredFields[channel].mode === 'or') {
            if (!this.isAttributeEmpty(messageObj, attribute)) {
              result = true
              return true
            }
          }
        })
      }
      return result
    }
  }
}
</script>

<style scoped>
.table-container.outlined {
  border: 1px solid #dbdee7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/CopyFromDialog.vue